import { lazy } from 'react'

/**
 * Lazily-loaded screens, kept apart from the router.
 *
 * Separate module so `router.tsx` exports only the route manifest: mixing
 * component definitions with a non-component export breaks fast refresh, and
 * a router that reloads the whole app on every edit is a slow afternoon.
 *
 * Everything except the dashboard is here. The POS and the dashboard are the
 * two screens people open constantly; reports drags in recharts, and the rest
 * is long-tail admin that should not sit in the bundle a cashier waits on.
 */

export const PosPage = lazy(() =>
  import('@/features/pos/pages/PosPage').then((m) => ({ default: m.PosPage })),
)

export const ProductsPage = lazy(() =>
  import('@/features/products/pages/ProductsPage').then((m) => ({ default: m.ProductsPage })),
)

export const CategoriesPage = lazy(() =>
  import('@/features/catalog/pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
)

export const StockPage = lazy(() =>
  import('@/features/inventory/pages/StockPage').then((m) => ({ default: m.StockPage })),
)

export const CustomersPage = lazy(() =>
  import('@/features/customers/pages/CustomersPage').then((m) => ({ default: m.CustomersPage })),
)

export const SalesPage = lazy(() =>
  import('@/features/sales/pages/SalesPage').then((m) => ({ default: m.SalesPage })),
)

export const ProfilePage = lazy(() =>
  import('@/features/auth/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)

export const SecurityPage = lazy(() =>
  import('@/features/auth/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })),
)

export const TablesPage = lazy(() =>
  import('@/features/pos/pages/TablesPage').then((m) => ({ default: m.TablesPage })),
)

export const RegistersPage = lazy(() =>
  import('@/features/registers/pages/RegistersPage').then((m) => ({ default: m.RegistersPage })),
)

export const ReportsPage = lazy(() =>
  import('@/features/reports/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
)

export const SupportPage = lazy(() =>
  import('@/features/support/pages/SupportPage').then((m) => ({ default: m.SupportPage })),
)

export const SupportTicketPage = lazy(() =>
  import('@/features/support/pages/SupportTicketPage').then((m) => ({
    default: m.SupportTicketPage,
  })),
)

export const BillingPage = lazy(() =>
  import('@/features/billing/pages/BillingPage').then((m) => ({ default: m.BillingPage })),
)

export const StaffPage = lazy(() =>
  import('@/features/org/pages/StaffPage').then((m) => ({ default: m.StaffPage })),
)

export const TaxesPage = lazy(() =>
  import('@/features/catalog/pages/TaxesPage').then((m) => ({ default: m.TaxesPage })),
)

export const SuppliersPage = lazy(() =>
  import('@/features/purchasing/pages/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
)

export const PurchasesPage = lazy(() =>
  import('@/features/purchasing/pages/PurchasesPage').then((m) => ({ default: m.PurchasesPage })),
)

export const TransfersPage = lazy(() =>
  import('@/features/transfers/pages/TransfersPage').then((m) => ({ default: m.TransfersPage })),
)

export const ReturnsPage = lazy(() =>
  import('@/features/sales/pages/ReturnsPage').then((m) => ({ default: m.ReturnsPage })),
)

export const RolesPage = lazy(() =>
  import('@/features/org/pages/RolesPage').then((m) => ({ default: m.RolesPage })),
)

export const OutletsPage = lazy(() =>
  import('@/features/org/pages/OutletsPage').then((m) => ({ default: m.OutletsPage })),
)

export const SettingsPage = lazy(() =>
  import('@/features/org/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)

/*
 * The operator console.
 *
 * Lazy as a whole, and mounted only under /platform. A cashier never loads a
 * byte of it — no screens, no second axios instance, and no platform token in
 * memory on a till.
 */

export const PlatformLoginPage = lazy(() =>
  import('@/features/platform/pages/PlatformLoginPage').then((m) => ({
    default: m.PlatformLoginPage,
  })),
)

export const PlatformDashboardPage = lazy(() =>
  import('@/features/platform/pages/PlatformDashboardPage').then((m) => ({
    default: m.PlatformDashboardPage,
  })),
)

export const PlatformTenantsPage = lazy(() =>
  import('@/features/platform/pages/PlatformTenantsPage').then((m) => ({
    default: m.PlatformTenantsPage,
  })),
)

export const PlatformTenantDetailPage = lazy(() =>
  import('@/features/platform/pages/PlatformTenantDetailPage').then((m) => ({
    default: m.PlatformTenantDetailPage,
  })),
)

export const PlatformPlansPage = lazy(() =>
  import('@/features/platform/pages/PlatformPlansPage').then((m) => ({
    default: m.PlatformPlansPage,
  })),
)

export const PlatformSubscriptionsPage = lazy(() =>
  import('@/features/platform/pages/PlatformSubscriptionsPage').then((m) => ({
    default: m.PlatformSubscriptionsPage,
  })),
)

export const PlatformWebhooksPage = lazy(() =>
  import('@/features/platform/pages/PlatformWebhooksPage').then((m) => ({
    default: m.PlatformWebhooksPage,
  })),
)

export const PlatformAdminsPage = lazy(() =>
  import('@/features/platform/pages/PlatformAdminsPage').then((m) => ({
    default: m.PlatformAdminsPage,
  })),
)

export const PlatformSettingsPage = lazy(() =>
  import('@/features/platform/pages/PlatformSettingsPage').then((m) => ({
    default: m.PlatformSettingsPage,
  })),
)

export const PlatformSupportPage = lazy(() =>
  import('@/features/platform/pages/PlatformSupportPage').then((m) => ({
    default: m.PlatformSupportPage,
  })),
)

export const PlatformSupportTicketPage = lazy(() =>
  import('@/features/platform/pages/PlatformSupportTicketPage').then((m) => ({
    default: m.PlatformSupportTicketPage,
  })),
)

export const OnboardingPage = lazy(() =>
  import('@/features/onboarding/pages/OnboardingPage').then((m) => ({
    default: m.OnboardingPage,
  })),
)

/* --- Finance -------------------------------------------------------------- */

export const ExpensesPage = lazy(() =>
  import('@/features/expenses/pages/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
)

export const ExpenseCategoriesPage = lazy(() =>
  import('@/features/expenses/pages/ExpenseCategoriesPage').then((m) => ({
    default: m.ExpenseCategoriesPage,
  })),
)

export const SupplierPaymentsPage = lazy(() =>
  import('@/features/supplier-payments/pages/SupplierPaymentsPage').then((m) => ({
    default: m.SupplierPaymentsPage,
  })),
)

export const PurchaseReturnsPage = lazy(() =>
  import('@/features/purchase-returns/pages/PurchaseReturnsPage').then((m) => ({
    default: m.PurchaseReturnsPage,
  })),
)

export const StockAdjustmentsPage = lazy(() =>
  import('@/features/stock-adjustments/pages/StockAdjustmentsPage').then((m) => ({
    default: m.StockAdjustmentsPage,
  })),
)

export const AccountingPage = lazy(() =>
  import('@/features/accounting/pages/AccountingPage').then((m) => ({ default: m.AccountingPage })),
)

export const ActivityLogPage = lazy(() =>
  import('@/features/activity/pages/ActivityLogPage').then((m) => ({
    default: m.ActivityLogPage,
  })),
)
